import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Avatar,
} from "@material-ui/core";
import VideocamOff from "@mui/icons-material/VideocamOff";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import { useCall } from "../../../context/CallContext";
import CallEndIcon from "@mui/icons-material/CallEnd";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  voiceCallContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    backgroundColor: "transparent",
    color: "white",
    height: "100%",
  },
  avatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    marginBottom: theme.spacing(2),
  },
}));

const VideoCallModal = ({ open, onClose }) => {
  const classes = useStyles();
  const {
    localStream,
    endCall,
    isInCall,
    isCalling,
    outgoingCallRequest,
    callType,
    callAccepted,
  } = useCall();
  const localVideoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const contact = outgoingCallRequest?.contact;
  const audioRef = useRef(null); // Ref for the audio element

  // Effect to manage local video stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Effect to manage dial tone audio
  useEffect(() => {
    // Only play dial tone if the call is outgoing and not yet accepted
    if (isCalling && !callAccepted) {
      const audio = new Audio("/sounds/phone-calling.mp3");
      audio.loop = true;
      audio.play().catch((err) => {
        console.warn("Autoplay failed:", err);
      });
      audioRef.current = audio;
    }

    // Cleanup function to stop the audio when the component unmounts
    // or when the call state changes
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [isCalling, callAccepted]); // Re-run effect when these states change

  const handleEndCall = () => {
    endCall();
    onClose();
  };

  const toggleMute = () => {
    if (!localStream) return;
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
    }
  };

  if (!open && !isInCall && !isCalling) return null;

  const isVoiceOnly = callType === "voice";

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      hideBackdrop={false}
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.7)" } }}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <DialogContent style={{ padding: "0" }}>
        {isVoiceOnly ? (
          <Box className={classes.voiceCallContainer}>
            <Avatar
              src={contact?.avatar}
              alt={contact?.name}
              className={classes.avatar}
            />
            <Typography variant="h5" style={{ color: "white" }}>
              Calling {contact?.name}...
            </Typography>
            <Typography
              variant="body1"
              style={{ color: "white", opacity: 0.8 }}
            >
              Voice Call
            </Typography>
          </Box>
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "16px",
            }}
          >
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              style={{
                width: "100%",
                maxWidth: "100%",
                height: "auto",
                borderRadius: "8px",
                border: "1px solid #ddd",
                backgroundColor: "#000",
                objectFit: "contain",
              }}
            />
          </div>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: "center", paddingBottom: 16 }}>
        <Tooltip title={isMuted ? "Unmute" : "Mute"} arrow>
          <IconButton onClick={toggleMute}>
            {isMuted ? (
              <MicOffIcon style={{ color: "white" }} />
            ) : (
              <MicIcon style={{ color: "white" }} />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="End Call" arrow>
          <Button
            onClick={handleEndCall}
            color="secondary"
            startIcon={<CallEndIcon style={{ color: "red" }} />}
          >
            End Call
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default VideoCallModal;
